import { useCallback, useMemo } from 'react';
import ReviewModal from '../layouts/ReviewModal';
import NoteReview from '../screens/NoteReview';
import { useSession } from '../contexts/SessionContext';
import {
  getDocumentationNoteType,
} from '../utils/taskDocumentation';
import { translateTaskItemLabel, translateTaskTitle } from '../i18n/displayLabels';
import { useLanguage } from '../contexts/LanguageContext';

function getTaskDisplayTitle(task, t) {
  if (!task) return '';
  if (task.itemLabel) return translateTaskItemLabel(t, task.itemLabel);
  return translateTaskTitle(t, task.title);
}

export default function TaskDocumentationModal({
  session,
  patient,
  onClose,
}) {
  const { t } = useLanguage();
  const {
    saveNote,
    saveCarePlan,
    clearTaskDocumentation,
    completeTask,
    setTaskDocumentationNotes,
  } = useSession();
  const draft = session?.draft || null;
  const task = session?.task;

  const noteType = useMemo(
    () => draft?.noteType || getDocumentationNoteType(task),
    [draft?.noteType, task]
  );

  const taskTitle = getTaskDisplayTitle(task, t);

  const handleDismiss = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleDiscard = useCallback(() => {
    clearTaskDocumentation();
    onClose?.();
  }, [clearTaskDocumentation, onClose]);

  const handleSaveNote = useCallback(
    (note) => {
      if (!session?.patientId) return;
      saveNote({
        ...note,
        patient_id: session.patientId,
        manualTaskCompletionTaskId: session.taskId,
      });
    },
    [saveNote, session]
  );

  const handleSaveCarePlan = useCallback(
    (payload) => {
      if (!payload?.patient_id || !payload?.note_content) return;
      saveCarePlan(payload.patient_id, payload.note_content, {
        documentationTime: payload.documentationTime,
        care_time: payload.care_time,
        care_day_offset: payload.care_day_offset,
      });
      if (session?.patientId && session?.taskId) {
        completeTask(session.patientId, session.taskId, new Date().toISOString(), 'manual');
      }
    },
    [completeTask, saveCarePlan, session]
  );

  const handleSaveComplete = useCallback(() => {
    clearTaskDocumentation();
    onClose?.();
  }, [clearTaskDocumentation, onClose]);

  const handleGeneratedNotesChange = useCallback(
    (generatedNotes) => {
      setTaskDocumentationNotes(generatedNotes);
    },
    [setTaskDocumentationNotes]
  );

  if (!session || !draft) return null;

  return (
    <ReviewModal onClose={handleDismiss}>
      <NoteReview
        key={draft.noteId || session.taskId}
        noteId={draft.noteId}
        encounterGroupId={draft.encounterGroupId}
        patientId={session.patientId}
        encounterDateTime={draft.encounterDateTime || new Date().toISOString()}
        transcript={draft.transcript}
        transcriptRaw={draft.transcriptRaw}
        transcriptCleaned={draft.transcriptCleaned}
        noteType={noteType}
        generatedNotes={draft.generatedNotes}
        openaiNote={draft.openaiNote}
        companionVitalsNote={draft.companionVitalsNote}
        cortiNote={draft.cortiNote}
        cortiRaw={draft.cortiRaw}
        cortiFailed={draft.cortiFailed ?? true}
        openaiError={draft.openaiError}
        manualTaskCompletionType={noteType}
        requireDocumentationBeforeSubmit
        hideFloatingMic
        hideEmptyTranscript
        taskTitle={taskTitle}
        taskPatient={patient}
        onGeneratedNotesChange={handleGeneratedNotesChange}
        onSaveNote={handleSaveNote}
        onSaveCarePlan={handleSaveCarePlan}
        onDiscard={handleDiscard}
        onSaveComplete={handleSaveComplete}
      />
    </ReviewModal>
  );
}
